import { useEffect, useMemo, useState } from "react";
import {
  Building2,
  ChevronRight,
  Circle,
  ClipboardList,
  DoorOpen,
  Home,
  Layers,
  Paintbrush,
  PanelsTopLeft,
  Percent,
  Ruler,
  Settings2,
  Trees,
  Wallet,
} from "lucide-react";

import FieldCard from "../../../../components/FieldCard";
import InputField from "../../../../components/InputField";
import WindowsDoorsEditor from "../../../../components/WindowsDoorsEditor";
import RoofSectionEditor from "../../../../components/RoofSectionEditor";
import FlooringEditor from "../../../../components/FlooringEditor";
import WallEditor from "../../../../components/WallEditor";
import TerraceEditor from "../../../../components/TerraceEditor";
import CeilingEditor from "../../../../components/CeilingEditor";
import PaintingEditor from "../../../../components/PaintingEditor";

const TABS = [
  { key: "general", label: "General", icon: Settings2 },
  { key: "carport_garage", label: "Carport / Garage", icon: Building2 },
  { key: "flooring", label: "Flooring", icon: Layers },
  { key: "wall", label: "Wall", icon: PanelsTopLeft },
  { key: "terrace", label: "Terrace", icon: Trees },
  { key: "ceiling", label: "Ceiling", icon: Home },
  { key: "windows_doors", label: "Windows / Doors", icon: DoorOpen },
  { key: "roof", label: "Roof", icon: Home },
  { key: "painting", label: "Painting", icon: Paintbrush },
];

function formatProjectTypeLabel(projectType) {
  switch (projectType) {
    case "carport_garage":
      return "Carport / Garage";
    case "flooring":
      return "Flooring";
    case "internal_wall":
      return "Internal wall";
    case "external_loadbearing_wall":
      return "Exterior loadbearing wall";
    case "wall":
      return "Wall";
    case "terrace":
      return "Terrace";
    case "ceiling":
      return "Ceiling";
    case "windows_doors":
      return "Windows / Doors";
    case "roof":
      return "Roof";
    case "painting":
      return "Painting";
    case "custom_manual":
    case "custom":
      return "Custom project";
    default:
      return projectType
        ? projectType.replace(/[_-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
        : "Unknown";
  }
}

function resolveProjectType(parsedData, estimate) {
  const calculator =
    estimate?.meta?.calculator ||
    estimate?.calculator ||
    parsedData?.meta?.calculator ||
    null;

  if (calculator && calculator !== "custom_manual") return calculator;

  const walls = Array.isArray(parsedData?.walls) ? parsedData.walls : [];
  const firstWall = walls[0] || {};

  const wallPlacement =
    firstWall?.wallPlacement ||
    parsedData?.wallPlacement ||
    (firstWall?.wallType === "external" ? "external" : null);

  const wallStructuralRole =
    firstWall?.wallStructuralRole || parsedData?.wallStructuralRole || null;

  if (wallPlacement === "external" && wallStructuralRole === "loadbearing") {
    return "external_loadbearing_wall";
  }

  if (
    parsedData?.projectType === "internal_wall" ||
    parsedData?.projectType === "wall" ||
    walls.length > 0
  ) {
    return "internal_wall";
  }

  const width = Number(parsedData?.widthM || parsedData?.structure?.widthM || 0);
  const length = Number(
    parsedData?.lengthM ||
      parsedData?.structure?.lengthM ||
      parsedData?.dimensions?.lengthM ||
      parsedData?.dimensions?.length ||
      estimate?.meta?.structure?.lengthM ||
      estimate?.meta?.structure?.length ||
      0
  );
  const height = Number(
    parsedData?.heightM ||
      parsedData?.structure?.maxHeightM ||
      parsedData?.structure?.heightM ||
      estimate?.meta?.structure?.maxHeightM ||
      estimate?.meta?.structure?.heightM ||
      0
  );
  const roofType = String(
    parsedData?.roofingType ||
      parsedData?.roof?.type ||
      estimate?.meta?.roof?.type ||
      ""
  ).toLowerCase();
  const roofArea = Number(
    parsedData?.roofAreaM2 ||
      parsedData?.roof?.roofAreaM2 ||
      estimate?.meta?.roof?.roofAreaM2 ||
      estimate?.meta?.roof?.areaM2 ||
      0
  );

  if (width > 0 && height > 0 && (length > 0 || roofArea > 0 || roofType === "tagpap")) {
    return "carport_garage";
  }

  return parsedData?.projectType || "custom";
}

function getDerivedStructure(parsedData = {}, estimate = {}) {
  const structure =
    parsedData?.structure && typeof parsedData.structure === "object"
      ? parsedData.structure
      : {};

  const metaStructure =
    estimate?.meta?.structure && typeof estimate.meta.structure === "object"
      ? estimate.meta.structure
      : {};

  const widthM =
    Number(structure?.widthM || 0) > 0
      ? Number(structure.widthM)
      : Number(parsedData?.widthM || metaStructure?.widthM || 0);

  const lengthM =
    Number(structure?.lengthM || 0) > 0
      ? Number(structure.lengthM)
      : Number(
          parsedData?.lengthM ||
            parsedData?.dimensions?.lengthM ||
            parsedData?.dimensions?.length ||
            metaStructure?.lengthM ||
            metaStructure?.length ||
            0
        );

  const maxHeightM =
    Number(structure?.maxHeightM || 0) > 0
      ? Number(structure.maxHeightM)
      : Number(
          parsedData?.heightM ||
            structure?.heightM ||
            metaStructure?.maxHeightM ||
            metaStructure?.heightM ||
            0
        );

  const footprintAreaM2 =
    Number(structure?.footprintAreaM2 || 0) > 0
      ? Number(structure.footprintAreaM2)
      : Number(parsedData?.floorAreaM2 || metaStructure?.footprintAreaM2 || 0);

  return {
    ...metaStructure,
    ...structure,
    widthM,
    lengthM,
    maxHeightM,
    footprintAreaM2,
    frameMaterial: structure?.frameMaterial || metaStructure?.frameMaterial || "",
    insulated: Boolean(structure?.insulated ?? metaStructure?.insulated),
    adaptedToExistingWall: Boolean(
      structure?.adaptedToExistingWall ?? metaStructure?.adaptedToExistingWall
    ),
    category: structure?.category || metaStructure?.category || "carport_garage",
  };
}

function getDerivedRoof(parsedData = {}, estimate = {}) {
  const roof =
    parsedData?.roof && typeof parsedData.roof === "object" ? parsedData.roof : {};

  const metaRoof =
    estimate?.meta?.roof && typeof estimate.meta.roof === "object"
      ? estimate.meta.roof
      : {};

  const roofType =
    roof?.type || metaRoof?.type || parsedData?.roofingType || parsedData?.roofType || "";

  const roofAreaM2 =
    Number(roof?.roofAreaM2 || 0) > 0
      ? Number(roof.roofAreaM2)
      : Number(parsedData?.roofAreaM2 || metaRoof?.roofAreaM2 || metaRoof?.areaM2 || 0);

  const included =
    roof?.included === true ||
    metaRoof?.included === true ||
    String(roofType).toLowerCase() === "tagpap" ||
    roofAreaM2 > 0;

  return {
    ...metaRoof,
    ...roof,
    type: roofType,
    roofAreaM2,
    included,
    pitchDegrees:
      Number(roof?.pitchDegrees || metaRoof?.pitchDegrees || 0) > 0
        ? Number(roof?.pitchDegrees || metaRoof?.pitchDegrees)
        : 0,
    fallRequired: Boolean(roof?.fallRequired ?? metaRoof?.fallRequired),
  };
}

function tabBadgeForKey(tabKey, parsedData, windows, doors, roofs, resolvedProjectType) {
  switch (tabKey) {
    case "carport_garage":
      return resolvedProjectType === "carport_garage" ? "Active" : null;
    case "flooring":
      return Number(parsedData?.floorAreaM2 || 0) > 0 ? `${parsedData.floorAreaM2} m²` : null;
    case "wall": {
      const walls = Array.isArray(parsedData?.walls) ? parsedData.walls : [];
      const firstWall = walls[0] || {};
      const isExterior =
        parsedData?.projectType === "external_loadbearing_wall" ||
        firstWall?.wallPlacement === "external" ||
        firstWall?.wallStructuralRole === "loadbearing";

      if (walls.length > 0) return isExterior ? `${walls.length} exterior` : `${walls.length} internal`;

      return Number(parsedData?.wallLengthM || 0) > 0 &&
        Number(parsedData?.wallHeightM || 0) > 0
        ? "1 section"
        : null;
    }
    case "terrace":
      return Number(parsedData?.terraceAreaM2 || 0) > 0 ||
        (Number(parsedData?.terraceLengthM || 0) > 0 &&
          Number(parsedData?.terraceWidthM || 0) > 0)
        ? "Configured"
        : null;
    case "ceiling":
      return Number(parsedData?.ceilingAreaM2 || 0) > 0 ? `${parsedData.ceilingAreaM2} m²` : null;
    case "windows_doors": {
      const total = (windows?.length || 0) + (doors?.length || 0);
      return total > 0 ? `${total}` : null;
    }
    case "roof":
      return (roofs?.length || 0) > 0 ? `${roofs.length}` : null;
    case "painting":
      return Number(parsedData?.paintingAreaM2 || 0) > 0 ? `${parsedData.paintingAreaM2} m²` : null;
    default:
      return null;
  }
}

function StatCard({ label, value, icon: Icon }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between gap-3">
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          {label}
        </div>
        {Icon && <Icon size={16} className="text-slate-400" />}
      </div>
      <div className="mt-2 truncate text-lg font-semibold text-slate-950">
        {value ?? "—"}
      </div>
    </div>
  );
}

function Panel({ title, subtitle, children, right }) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-5 py-4">
        <div>
          <h2 className="text-sm font-semibold text-slate-950">{title}</h2>
          {subtitle && <p className="mt-0.5 text-xs text-slate-500">{subtitle}</p>}
        </div>
        {right}
      </div>
      <div className="p-5">{children}</div>
    </section>
  );
}

function CommercialField({ label, children }) {
  return (
    <div>
      <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
        {label}
      </div>
      {children}
    </div>
  );
}

export default function ScopeWorkspace({
  quote,
  estimate,
  parsedData,
  setParsedData,
  errors,
  windows,
  setWindows,
  doors,
  setDoors,
  roofs,
  setRoofs,
  isBusy = false,
  onChangeMarginPercent,
}) {
  const [activeTab, setActiveTab] = useState("general");

  const resolvedProjectType = resolveProjectType(parsedData, estimate);
  const projectTypeLabel = formatProjectTypeLabel(resolvedProjectType);
  const derivedStructure = getDerivedStructure(parsedData, estimate);
  const derivedRoof = getDerivedRoof(parsedData, estimate);

  useEffect(() => {
    const currentRoofs = Array.isArray(parsedData?.roofs) ? parsedData.roofs : [];
    if (currentRoofs.length > 0) return;

    const roofArea = Number(derivedRoof?.roofAreaM2 || 0);
    const roofType = derivedRoof?.type || "";

    if (roofArea <= 0 && !roofType) return;

    setParsedData((prev) => {
      const prevRoofs = Array.isArray(prev?.roofs) ? prev.roofs : [];
      if (prevRoofs.length > 0) return prev;

      return {
        ...(prev || {}),
        roofs: [
          {
            id: crypto.randomUUID(),
            name: "Main roof",
            areaM2: roofArea || "",
            roofType: roofType || "tagpap",
            pitch:
              Number(derivedRoof?.pitchDegrees || 0) > 20
                ? "steep"
                : Number(derivedRoof?.pitchDegrees || 0) > 5
                ? "medium"
                : "flat",
            complexity: "standard",
          },
        ],
      };
    });
  }, [
    parsedData?.roofs?.length,
    derivedRoof?.roofAreaM2,
    derivedRoof?.type,
    derivedRoof?.pitchDegrees,
    setParsedData,
  ]);

  const editableRoofs = Array.isArray(parsedData?.roofs) ? parsedData.roofs : [];

  const handleParsedChange = (field, value) => {
    setParsedData((prev) => ({
      ...(prev || {}),
      [field]: value,
    }));
  };

  const handleParsedNestedChange = (section, field, value) => {
    setParsedData((prev) => ({
      ...(prev || {}),
      [section]: {
        ...((prev || {})[section] || {}),
        [field]: value,
      },
    }));
  };

  const summaryStats = useMemo(
    () => [
      {
        label: "Project type",
        value: projectTypeLabel,
        icon: ClipboardList,
      },
      {
        label: "Flooring",
        value: Number(parsedData?.floorAreaM2 || 0) > 0 ? `${parsedData.floorAreaM2} m²` : "—",
        icon: Layers,
      },
      {
        label: "Walls",
        value: Array.isArray(parsedData?.walls) ? parsedData.walls.length : 0,
        icon: PanelsTopLeft,
      },
      {
        label: "Windows / Doors",
        value: `${windows?.length || 0} / ${doors?.length || 0}`,
        icon: DoorOpen,
      },
      {
        label: "Width",
        value: derivedStructure?.widthM ? `${derivedStructure.widthM} m` : "—",
        icon: Ruler,
      },
      {
        label: "Length",
        value: derivedStructure?.lengthM ? `${derivedStructure.lengthM} m` : "—",
        icon: Ruler,
      },
      {
        label: "Height",
        value: derivedStructure?.maxHeightM ? `${derivedStructure.maxHeightM} m` : "—",
        icon: Ruler,
      },
      {
        label: "Roof area",
        value: derivedRoof?.roofAreaM2 ? `${derivedRoof.roofAreaM2} m²` : "—",
        icon: Home,
      },
    ],
    [projectTypeLabel, parsedData, windows, doors, derivedStructure, derivedRoof]
  );

  const renderEditor = () => {
    switch (activeTab) {
      case "general":
        return (
          <div className="space-y-5">
            <Panel title="Project" subtitle="Basic project and customer information.">
              <div className="grid gap-4 lg:grid-cols-3">
                <FieldCard label="Project Name" error={errors?.projectName}>
                  <InputField
                    value={parsedData?.projectName || ""}
                    onChange={(e) => handleParsedChange("projectName", e.target.value)}
                    disabled={isBusy}
                  />
                </FieldCard>

                <FieldCard label="Customer">
                  <InputField
                    value={parsedData?.customerName || quote?.customer_name || ""}
                    onChange={(e) => handleParsedChange("customerName", e.target.value)}
                    disabled={isBusy}
                  />
                </FieldCard>

                <FieldCard label="Project Type">
                  <InputField value={projectTypeLabel} disabled />
                </FieldCard>
              </div>
            </Panel>

            <Panel title="Commercial settings" subtitle="Pricing basis used by the estimate.">
              <div className="grid gap-4 lg:grid-cols-4">
                <CommercialField label="Margin">
                  <div className="relative">
                    <InputField
                      type="number"
                      value={estimate?.meta?.marginPercent ?? parsedData?.marginPercent ?? 25}
                      onChange={(e) => {
                        const value = Number(e.target.value);
                        handleParsedChange("marginPercent", value);
                        onChangeMarginPercent?.(value);
                      }}
                      disabled={isBusy}
                    />
                    <Percent
                      size={14}
                      className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                    />
                  </div>
                </CommercialField>

                <CommercialField label="Country">
                  <select
                    value={parsedData?.country || "DK"}
                    onChange={(e) => {
                      handleParsedChange("country", e.target.value);
                      handleParsedChange("currency", e.target.value === "SE" ? "SEK" : "DKK");
                    }}
                    disabled={isBusy}
                    className="h-9 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  >
                    <option value="DK">Denmark</option>
                    <option value="SE">Sweden</option>
                  </select>
                </CommercialField>

                <CommercialField label="Currency">
                  <select
                    value={parsedData?.currency || "DKK"}
                    onChange={(e) => handleParsedChange("currency", e.target.value)}
                    disabled={isBusy}
                    className="h-9 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  >
                    <option value="DKK">DKK</option>
                    <option value="SEK">SEK</option>
                  </select>
                </CommercialField>

                <CommercialField label="Finish Level">
                  <select
                    value={parsedData?.finishLevel || "standard"}
                    onChange={(e) => handleParsedChange("finishLevel", e.target.value)}
                    disabled={isBusy}
                    className="h-9 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  >
                    <option value="standard">Standard</option>
                    <option value="premium">Premium</option>
                  </select>
                </CommercialField>
              </div>
            </Panel>

            <Panel title="Scope overview" subtitle="Current interpreted project scope.">
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                {summaryStats.map((item) => (
                  <StatCard
                    key={item.label}
                    label={item.label}
                    value={item.value}
                    icon={item.icon}
                  />
                ))}
              </div>
            </Panel>
          </div>
        );

      case "carport_garage":
        return (
          <Panel title="Carport / Garage" subtitle="Structure dimensions and roof assumptions.">
            <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-4">
              <FieldCard label="Width" unit="m">
                <InputField
                  type="number"
                  value={parsedData?.widthM ?? derivedStructure?.widthM ?? ""}
                  onChange={(e) => handleParsedChange("widthM", Number(e.target.value))}
                  disabled={isBusy}
                />
              </FieldCard>

              <FieldCard label="Length" unit="m">
                <InputField
                  type="number"
                  value={parsedData?.lengthM ?? derivedStructure?.lengthM ?? ""}
                  onChange={(e) => handleParsedChange("lengthM", Number(e.target.value))}
                  disabled={isBusy}
                />
              </FieldCard>

              <FieldCard label="Height" unit="m">
                <InputField
                  type="number"
                  value={parsedData?.heightM ?? derivedStructure?.maxHeightM ?? ""}
                  onChange={(e) => handleParsedChange("heightM", Number(e.target.value))}
                  disabled={isBusy}
                />
              </FieldCard>

              <FieldCard label="Roof type">
                <InputField
                  value={parsedData?.roofingType || derivedRoof?.type || ""}
                  onChange={(e) => handleParsedChange("roofingType", e.target.value)}
                  disabled={isBusy}
                />
              </FieldCard>

              <FieldCard label="Frame material">
                <InputField
                  value={parsedData?.structure?.frameMaterial || derivedStructure?.frameMaterial || ""}
                  onChange={(e) =>
                    handleParsedNestedChange("structure", "frameMaterial", e.target.value)
                  }
                  disabled={isBusy}
                />
              </FieldCard>

              <FieldCard label="Roof pitch" unit="°">
                <InputField
                  type="number"
                  value={parsedData?.roof?.pitchDegrees ?? derivedRoof?.pitchDegrees ?? ""}
                  onChange={(e) =>
                    handleParsedNestedChange("roof", "pitchDegrees", Number(e.target.value))
                  }
                  disabled={isBusy}
                />
              </FieldCard>
            </div>
          </Panel>
        );

      case "flooring":
        return <FlooringEditor parsedData={parsedData} setParsedData={setParsedData} errors={errors} />;

      case "wall":
        return <WallEditor parsedData={parsedData} setParsedData={setParsedData} errors={errors} />;

      case "terrace":
        return <TerraceEditor parsedData={parsedData} setParsedData={setParsedData} errors={errors} />;

      case "ceiling":
        return <CeilingEditor parsedData={parsedData} setParsedData={setParsedData} errors={errors} />;

      case "windows_doors":
        return (
          <WindowsDoorsEditor
            windows={windows}
            setWindows={setWindows}
            doors={doors}
            setDoors={setDoors}
            errors={errors}
          />
        );

      case "roof":
        return (
          <RoofSectionEditor
            roofs={editableRoofs}
            setRoofs={(nextRoofs) => {
              setParsedData((prev) => {
                const currentRoofs = Array.isArray(prev?.roofs) ? prev.roofs : [];
                const resolvedRoofs =
                  typeof nextRoofs === "function" ? nextRoofs(currentRoofs) : nextRoofs;

                return {
                  ...(prev || {}),
                  roofs: Array.isArray(resolvedRoofs) ? resolvedRoofs : [],
                };
              });
            }}
            errors={errors}
          />
        );

      case "painting":
        return <PaintingEditor parsedData={parsedData} setParsedData={setParsedData} errors={errors} />;

      default:
        return null;
    }
  };

  return (
  <div className="h-full overflow-hidden bg-[#f3f2f1] text-[#201f1e]">
    <div className="flex h-full min-h-0">
      <aside className="hidden w-72 shrink-0 border-r border-[#edebe9] bg-white lg:block">
        <div className="border-b border-[#edebe9] px-5 py-5">
          <div className="text-xs font-semibold uppercase tracking-wide text-[#605e5c]">
            Scope editor
          </div>
          <div className="mt-1 text-lg font-semibold text-[#201f1e]">
            {projectTypeLabel}
          </div>
        </div>

        <nav className="space-y-1 p-3">
          {TABS.map((tab) => {
            const Icon = tab.icon || Circle;
            const isActive = activeTab === tab.key;
            const badge = tabBadgeForKey(
              tab.key,
              parsedData,
              windows,
              doors,
              editableRoofs,
              resolvedProjectType
            );

            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={[
                  "flex w-full items-center justify-between gap-3 rounded-sm px-3 py-2.5 text-left text-sm transition",
                  isActive
                    ? "bg-[#eef6fc] text-[#0f6cbd]"
                    : "text-[#605e5c] hover:bg-[#f3f2f1] hover:text-[#201f1e]",
                ].join(" ")}
              >
                <span className="flex min-w-0 items-center gap-3">
                  <Icon
                    size={17}
                    className={isActive ? "text-[#0f6cbd]" : "text-[#8a8886]"}
                  />
                  <span className="truncate font-medium">{tab.label}</span>
                </span>

                <span className="flex items-center gap-2">
                  {badge && (
                    <span
                      className={[
                        "rounded-sm px-2 py-0.5 text-[10px] font-semibold",
                        isActive
                          ? "bg-[#dbeaf7] text-[#0f6cbd]"
                          : "bg-[#f3f2f1] text-[#605e5c]",
                      ].join(" ")}
                    >
                      {badge}
                    </span>
                  )}
                  {isActive && (
                    <ChevronRight size={15} className="text-[#0f6cbd]" />
                  )}
                </span>
              </button>
            );
          })}
        </nav>
      </aside>

      <main className="min-w-0 flex-1 overflow-auto">
        <div className="mx-auto max-w-7xl space-y-5 p-4 lg:p-6">
          <div className="rounded-sm border border-[#edebe9] bg-white p-5 shadow-sm">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-[#605e5c]">
                  Project scope
                </div>
                <h1 className="mt-1 text-2xl font-semibold tracking-tight text-[#201f1e]">
                  {parsedData?.projectName ||
                    quote?.customer_name ||
                    "Untitled project"}
                </h1>
                <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-[#605e5c]">
                  <span>{projectTypeLabel}</span>
                  <span>•</span>
                  <span>{parsedData?.country || "DK"}</span>
                  <span>•</span>
                  <span>{parsedData?.currency || "DKK"}</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <div className="rounded-sm border border-[#edebe9] bg-white px-4 py-3">
                  <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-[#605e5c]">
                    <Wallet size={14} />
                    Margin
                  </div>
                  <div className="mt-1 text-lg font-semibold text-[#201f1e]">
                    {estimate?.meta?.marginPercent ??
                      parsedData?.marginPercent ??
                      25}
                    %
                  </div>
                </div>

                <div className="rounded-sm border border-[#edebe9] bg-white px-4 py-3">
                  <div className="text-xs font-semibold uppercase tracking-wide text-[#605e5c]">
                    Roof
                  </div>
                  <div className="mt-1 text-lg font-semibold text-[#201f1e]">
                    {derivedRoof?.included ? "Included" : "—"}
                  </div>
                </div>

                <div className="rounded-sm border border-[#edebe9] bg-white px-4 py-3">
                  <div className="text-xs font-semibold uppercase tracking-wide text-[#605e5c]">
                    Windows
                  </div>
                  <div className="mt-1 text-lg font-semibold text-[#201f1e]">
                    {windows?.length || 0}
                  </div>
                </div>

                <div className="rounded-sm border border-[#edebe9] bg-white px-4 py-3">
                  <div className="text-xs font-semibold uppercase tracking-wide text-[#605e5c]">
                    Doors
                  </div>
                  <div className="mt-1 text-lg font-semibold text-[#201f1e]">
                    {doors?.length || 0}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:hidden">
            <select
              value={activeTab}
              onChange={(event) => setActiveTab(event.target.value)}
              className="h-11 w-full rounded-sm border border-[#edebe9] bg-white px-3 text-sm font-medium text-[#201f1e] shadow-sm"
            >
              {TABS.map((tab) => (
                <option key={tab.key} value={tab.key}>
                  {tab.label}
                </option>
              ))}
            </select>
          </div>

          {renderEditor()}
        </div>
      </main>
    </div>
  </div>
);
}